import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MeasurementGroupLoaderResponse, MeasurementGroupStatusUpdateRequest } from '../types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RHFDateTimePicker } from '@/components/date-picker/RHFDateTimePicker';
import { getCustomerMessageOptions, getMeasurementGroupStatusForm } from './mg-status-update-zod-schema';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { Suspense, useState } from 'react';
import { Await, useLoaderData, useRevalidator } from 'react-router';
import Spinner from '@/components/spinner/Spinner';
import { Textarea } from '@/components/ui/textarea';
import { useMutation } from '@tanstack/react-query';
import { updateMeasurementGroupStatus } from '../constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { Badge } from '@/components/ui/badge';

type MeasurementGroupStatusUpdateProps = {
	name: string;
	status: string;
	id: number;
	isOpen: boolean;
	handleToggleForm: () => void;
};

const MeasurementGroupStatusUpdateForm = (props: MeasurementGroupStatusUpdateProps) => {
	const { name, status, id, isOpen, handleToggleForm } = props;

	const { measurementGroupStatuses } = useLoaderData() as MeasurementGroupLoaderResponse;
	const { isDesktop } = useIsDesktopSize();

	const revalidator = useRevalidator();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const customerMessageOptions = getCustomerMessageOptions(name);

	const measurementGroupStatusForm = getMeasurementGroupStatusForm(name);

	const statusUpdateForm = useForm({
		resolver: zodResolver(measurementGroupStatusForm),
		defaultValues: {
			statusId: '',
			internalMessage: '',
			customerMessage: '',
			additionalCustomerMessage: '',
			startDate: undefined,
			returnDate: undefined
		}
	});

	const { isPending: isUpdating, mutate: updateStatus } = useMutation({
		mutationFn: (payload: MeasurementGroupStatusUpdateRequest) => updateMeasurementGroupStatus(id, payload),
		mutationKey: ['measurement-groups'],
		onSuccess: () => {
			statusUpdateForm.reset();
			handleCloseForm();
			toast('Measurement group status updated successfully');
			revalidator.revalidate();
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleUpdateStatus = () => {
		const submitForm = statusUpdateForm.handleSubmit(values => {
			const customerMessage = getCustomerMessage(values.customerMessage, values.additionalCustomerMessage);

			const payload: MeasurementGroupStatusUpdateRequest = {
				statusId: parseInt(values.statusId),
				internalMessage: values.internalMessage,
				customerMessage: customerMessage,
				startDate: values.startDate ?? null,
				returnDate: values.returnDate ?? null
			};
			updateStatus(payload);
		});
		submitForm();
	};

	const getCustomerMessage = (message: string, additionalMessage: string) => {
		if (message === 'Custom message') {
			return additionalMessage;
		}
		if (!additionalMessage.trim()) {
			return message;
		}
		return message + '. ' + additionalMessage;
	};

	const customerMessageValue = statusUpdateForm.watch('customerMessage');
	const isAdditionalCustomerMessageRequired = customerMessageValue === customerMessageOptions[3];
	const isAdditionalCustomerMessageVisible =
		customerMessageValue === customerMessageOptions[2] || customerMessageValue === customerMessageOptions[3];

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button
					asChild
					type="button"
					size="sm"
					variant="outline"
					onClick={handleToggleForm}
					className="flex justify-start min-w-32 text-xs"
				>
					<span>
						<Settings />
						Update status
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn(' p-5 overflow-auto md:max-w-xl md:w-xl md:h-full w-full h-5/6')}
			>
				<SheetHeader>
					<SheetTitle>Update measurement group status</SheetTitle>
					<SheetDescription>Fill in the details. Click save when you're done.</SheetDescription>
				</SheetHeader>

				<Form {...statusUpdateForm}>
					<form className="p-4 flex flex-col gap-3">
						<div className="text-sm flex gap-2 items-center">
							<p className="font-medium">Current status</p>
							<Badge variant="success" className="min-w-20">
								{status}
							</Badge>
						</div>
						<FormField
							control={statusUpdateForm.control}
							name="statusId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>New status*</FormLabel>

									<Select onValueChange={field.onChange} value={field.value}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select a status" />
											</SelectTrigger>
										</FormControl>
										<Suspense
											fallback={
												<SelectContent>
													<Spinner />
												</SelectContent>
											}
										>
											<Await resolve={measurementGroupStatuses}>
												{measurementGroupStatuses => {
													return (
														<SelectContent>
															{measurementGroupStatuses.data.map(s => (
																<SelectItem
																	key={s.statusId}
																	value={s.statusId.toString()}
																>
																	{s.statusName}
																</SelectItem>
															))}
														</SelectContent>
													);
												}}
											</Await>
										</Suspense>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={statusUpdateForm.control}
							name="internalMessage"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Internal message</FormLabel>
									<FormControl>
										<Textarea
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											placeholder="Add an internal message here"
											rows={4}
											className="text-sm"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={statusUpdateForm.control}
							name="customerMessage"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Customer message*</FormLabel>

									<Select onValueChange={field.onChange} value={field.value ?? ''}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="Select a customer message" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{customerMessageOptions.map(opt => (
												<SelectItem key={opt} value={opt.toString()}>
													{opt}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						{isAdditionalCustomerMessageVisible && (
							<FormField
								control={statusUpdateForm.control}
								name="additionalCustomerMessage"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Additional customer message
											{isAdditionalCustomerMessageRequired ? '*' : ''}
										</FormLabel>
										<FormControl>
											<Textarea
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
												placeholder="Add any notes here"
												rows={4}
												className="text-sm"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<RHFDateTimePicker form={statusUpdateForm} name="startDate" label="Start date" />
						<RHFDateTimePicker form={statusUpdateForm} name="returnDate" label="Return date" />
					</form>
				</Form>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to save the changes?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isUpdating}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleUpdateStatus}
									disabled={isUpdating}
								>
									{error ? 'Try again ' : 'Save'}
									{isUpdating && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};

export default MeasurementGroupStatusUpdateForm;
